// src/pages/Login.js
import React, { useState, useEffect } from 'react';
import {
    TextField,
    Typography,
    Paper,
    Box,
    Container,
    useMediaQuery,
    Snackbar,
    Alert
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/system';
import { login as apiLogin } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Styled components with responsive design
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: '8px',
    boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.04)',
    width: '100%',
    maxWidth: '436px',
    margin: '0 auto',
    backgroundColor: '#FFFFFF'
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    width: '100%',
    '& .MuiOutlinedInput-root': {
        height: '44px',
        borderRadius: '8px',
        '& fieldset': {
            borderColor: '#DADCE0'
        },
        '&:hover fieldset': {
            borderColor: '#DADCE0'
        }
    }
}));

// Base button style for consistency
const BaseButton = styled('button')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0',
    width: '100%',
    height: '44px',
    borderRadius: '8px',
    cursor: 'pointer',
    marginBottom: '10px',
    fontFamily: 'Inter, sans-serif'
}));

// Custom styled Google sign-in button
const GoogleButton = styled(BaseButton)(({ theme }) => ({
    background: '#FFFFFF',
    border: '1px solid #DADCE0',
    '&:hover': {
        background: '#F5F5F5'
    },
    '&:active': {
        background: '#F5F5F5',
        boxShadow: '2px 4px 4px rgba(0, 0, 0, 0.04)'
    }
}));

// Apple button style
const AppleButton = styled(BaseButton)(({ theme }) => ({
    background: '#FFFFFF',
    border: '1px solid #DADCE0',
    '&:hover': {
        background: '#F5F5F5'
    },
    '&:active': {
        background: '#F5F5F5',
        boxShadow: '2px 4px 4px rgba(0, 0, 0, 0.04)'
    }
}));

// Logo image with responsive sizing
const LogoImage = styled('img')(({ theme }) => ({
    width: '220px',
    maxWidth: '90%',
    height: 'auto',
    marginBottom: '10px'
}));

// Button icon
const ButtonIcon = styled('img')(({ theme }) => ({
    width: '24px',
    height: '24px',
    marginRight: '10px'
}));

// Button text
const ButtonText = styled('span')(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '19px',
    color: '#181D27',
    marginLeft: '8px'
}));

// Page container
const PageContainer = styled(Box)(({ theme }) => ({
    backgroundColor: '#F5F8FA',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5)
}));

// Login button
const LoginButton = styled(BaseButton)(({ theme }) => ({
    backgroundColor: '#69BD7B',
    color: 'rgba(255, 255, 255, 0.9)',
    border: 'none',
    fontSize: '16px',
    fontWeight: 500,
    '&:hover': {
        backgroundColor: '#5BAD6D'
    },
    '&:disabled': {
        backgroundColor: '#A9D6B3',
        cursor: 'not-allowed'
    }
}));

// Form container
const FormContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
}));

// Typing Animation Component
const TypingAnimation = ({ texts, typingSpeed = 100, pauseTime = 1500 }) => {
    const [textIndex, setTextIndex] = useState(0);
    const [currentText, setCurrentText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            const currentFullText = texts[textIndex];

            // Typing
            if (!isDeleting && currentText.length < currentFullText.length) {
                setCurrentText(currentFullText.substring(0, currentText.length + 1));
            }
            // Pausing after typing
            else if (!isDeleting && currentText === currentFullText) {
                setTimeout(() => setIsDeleting(true), pauseTime);
            }
            // Deleting
            else if (isDeleting && currentText.length > 0) {
                setCurrentText(currentText.substring(0, currentText.length - 1));
            }
            // Move to next text
            else if (isDeleting && currentText === '') {
                setIsDeleting(false);
                setTextIndex((textIndex + 1) % texts.length);
            }
        }, isDeleting ? typingSpeed / 2 : typingSpeed);

        return () => clearTimeout(timeout);
    }, [currentText, isDeleting, textIndex, texts, typingSpeed, pauseTime]);

    return (
        <Typography
            component="span"
            sx={{
                color: '#69BD7B',
                fontFamily: 'Inter, sans-serif',
                fontSize: { xs: '20px', sm: '24px' },
                fontWeight: 400
            }}
        >
            {currentText}
            <span style={{ opacity: 1 }}>|</span>
        </Typography>
    );
};

function Login() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({
        username: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const navigate = useNavigate();
    const location = useLocation();
    const { checkLoginStatus } = useAuth();
    const isMobile = useMediaQuery('(max-width:600px)');

    // Redirect to /listings if already logged in
    useEffect(() => {
        const verifyLoggedIn = async () => {
            const token = localStorage.getItem('access_token');
            if (token) {
                navigate('/listings');
                return;
            }

            // If your AuthContext checkLoginStatus returns a boolean, you can use:
            // const isLoggedIn = await checkLoginStatus();
            // if (isLoggedIn) {
            //   navigate('/listings');
            // }
        };
        verifyLoggedIn();
    }, [checkLoginStatus, navigate]);

    // Check for redirect message in location state
    useEffect(() => {
        if (location.state?.message) {
            setSnackbar({
                open: true,
                message: location.state.message,
                severity: location.state.severity || 'info'
            });

            // Clear the state after showing the message
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const validateForm = () => {
        const errors = {};
        if (!username.trim()) {
            errors.username = 'Username is required';
        }
        if (!password.trim()) {
            errors.password = 'Password is required';
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const expoToken = localStorage.getItem('expoToken') || '';
            const response = await apiLogin(username, password, expoToken);
            localStorage.setItem('access_token', response.data.access_token);
            localStorage.setItem('refresh_token', response.data.refresh_token);
            await checkLoginStatus();
            navigate('/listings');
        } catch (error) {
            console.error('Login failed', error);

            if (error.response) {
                if (error.response.status === 401) {
                    setError('Invalid username or password. Please try again.');
                } else if (error.response.status === 403) {
                    setError('Your account is not active. Please check your email for verification link.');
                } else if (error.response.status >= 500) {
                    setError('Server error. Please try again later.');
                } else {
                    setError('Login failed. Please check your credentials and try again.');
                }
            } else if (error.request) {
                setError('Network error. Please check your internet connection.');
            } else {
                setError('Login failed. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = () => {
        window.location.href = process.env.REACT_APP_API_URL + '/auth/google/login';
    };

    const handleAppleLogin = () => {
        window.location.href = process.env.REACT_APP_API_URL + '/auth/apple/login';
    };

    return (
        <PageContainer>
            <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                    <LogoImage src={`${process.env.PUBLIC_URL}/logo_with_name.png`} alt="Borrowed Logo" />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: 'center',
                            mt: 1,
                            textAlign: isMobile ? 'center' : 'left'
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: { xs: '20px', sm: '24px' },
                                fontWeight: 400,
                                mr: isMobile ? 0 : 1
                            }}
                        >
                            Log in to start
                        </Typography>
                        <Box component="span" sx={{ ml: isMobile ? 0 : 1 }}>
                            <TypingAnimation texts={['Lending!', 'Borrowing!']} typingSpeed={50} pauseTime={1500} />
                        </Box>
                    </Box>
                </Box>

                <StyledPaper elevation={0}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <FormContainer>
                        <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: '390px' }}>
                            <StyledTextField
                                variant="outlined"
                                required
                                fullWidth
                                placeholder="Username"
                                value={username}
                                onChange={(e) => {
                                    setUsername(e.target.value);
                                    setValidationErrors((prev) => ({ ...prev, username: '' }));
                                }}
                                error={!!validationErrors.username}
                                helperText={validationErrors.username}
                                InputProps={{
                                    style: { fontSize: '16px' }
                                }}
                                disabled={loading}
                            />

                            <StyledTextField
                                variant="outlined"
                                required
                                fullWidth
                                placeholder="Password"
                                type="password"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setValidationErrors((prev) => ({ ...prev, password: '' }));
                                }}
                                error={!!validationErrors.password}
                                helperText={validationErrors.password}
                                InputProps={{
                                    style: { fontSize: '16px' }
                                }}
                                disabled={loading}
                            />

                            <LoginButton type="submit" disabled={loading}>
                                {loading ? 'Logging in...' : 'Log in'}
                            </LoginButton>

                            <GoogleButton onClick={handleGoogleLogin} disabled={loading}>
                                <ButtonIcon
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg"
                                    alt="Google Logo"
                                />
                                <ButtonText>Sign in with Google</ButtonText>
                            </GoogleButton>

                            <AppleButton onClick={handleAppleLogin} disabled={loading}>
                                <ButtonIcon src={`${process.env.PUBLIC_URL}/apple-logo.png`} alt="Apple Logo" />
                                <ButtonText>Sign in with Apple</ButtonText>
                            </AppleButton>
                        </form>
                    </FormContainer>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mt: 3,
                            flexDirection: isMobile ? 'column' : 'row'
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '15px',
                                fontFamily: 'Inter, sans-serif',
                                mr: isMobile ? 0 : 1,
                                mb: isMobile ? 1 : 0
                            }}
                        >
                            Don't have an account?
                        </Typography>
                        <Link
                            to="/register"
                            style={{
                                color: '#69BD7B',
                                textDecoration: 'none',
                                fontSize: '15px',
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500
                            }}
                        >
                            Sign up
                        </Link>
                    </Box>
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Link
                            to="/password-recovery"
                            style={{
                                color: '#69BD7B',
                                textDecoration: 'none',
                                fontSize: '14px',
                                fontFamily: 'Inter, sans-serif'
                            }}
                        >
                            Forgot password?
                        </Link>
                    </Box>
                </StyledPaper>
            </Container>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </PageContainer>
    );
}

export default Login;
